/// <amd-module name="Release/Landing/HubContent/Deployments/Deployments" />
import * as React from "react";

import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";
import { Link } from "VSSUI/Link";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import { format, equals } from "VSS/Core/Util/String";

import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { getAssetLocation } from "VSS/Platform/Location";
import { VssComponent, IVssComponentProperties, IVssComponentContext } from "VSS/Platform/Layout";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";

import { getTFSPageData } from "Tfs/Platform//Page";

import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { Deployment, DeploymentOperationStatus, EnvironmentStatus, DeploymentStatus } from "Release/Client/Contracts/Release";

import { ActiveDefinitionReferenceType } from "DataProviderContracts";
import { DeploymentsActionCreator } from "./ActionCreator";
import { DeploymentsStore, IDeploymentsState } from "./Store";
import { DeploymentsDetailsList } from "./DeploymentsDetailsList";
import { IReleasesSignalRHandler, getReleasesSignalRHub } from "Release/Landing/SignalR/LandingSignalRHub";
import { IDeploymentsFilterState } from "../Filter/FilterTypes";
import * as Resources from "Release/Landing/Resources";

export interface IDeploymentsComponentProps extends IVssComponentProperties {
    definitionId: number;
    definitionName: string;
    folderPath: string;
    isSearchedItem: boolean;
    definitionReferenceType: ActiveDefinitionReferenceType;
    endPerfScenarioCallback: () => void;
    filterState: IDeploymentsFilterState;
}

let _onScrollRerenderTimeoutId: number = 0;

export class DeploymentsView extends VssComponent<IDeploymentsComponentProps, IDeploymentsState>{
    constructor(props: IDeploymentsComponentProps, context: IVssComponentContext) {
        super(props, context);

        this._deploymentsActionCreator = ActionCreatorManager.GetActionCreator<DeploymentsActionCreator>(DeploymentsActionCreator);
        this._deploymentsStore = StoreManager.GetStore<DeploymentsStore>(DeploymentsStore);

        // The statement below is going to make an API call the first time, or fetch data from the data provider
        this._deploymentsActionCreator.getDeployments(this.context.pageContext, this.props.definitionId, this.props.filterState);

        // Add changed handler to the store after the first set of deployments is fetched. This would prevent a changed handler firing a setState
        // while we are in the flow where we are fetching deployments. For async calls, the store will be updated in a later process queue, and the
        // view will be updated in a later setState. When data is in the data provider, the store will be updated synchronously before the
        // we add the changed handler below.
        this._deploymentsStore.addChangedListener(this._onDeploymentsStoreUpdate);

        const storeState = this._deploymentsStore.getState();

        if (props.definitionId === storeState.definitionId) {
            this.state = storeState;
        }
        else {
            this.state = {
                continuationToken: 0,
                definitionId: props.definitionId,
                deployments: [],
                environments: [],
                isLoading: true
            };
        }
    }

    public componentDidMount(): void {
        if (!this.state.isLoading) {
            this.props.endPerfScenarioCallback();

            // Check if there is space remaining. Fetch next batch in that case
            setTimeout(() => this._fetchMoreDeploymentsIfThereIsSpace(), 750); // Wait for 750 ms to sufficiently allow virtualized rendering to complete  
        }

        this._signalRHandler = new DeploymentsListSignalRHandler(this.context.pageContext, this._deploymentsActionCreator, this._deploymentsStore);
        const signalRHub = getReleasesSignalRHub();
        if (signalRHub) {
            signalRHub.addSignalRHanlder(this._signalRHandler);
            this._subscribeToPipeline(this.props.definitionId);
        }
    }

    public componentWillReceiveProps(nextProps: IDeploymentsComponentProps): void {
        if (nextProps.definitionId !== this.props.definitionId) {
            this._deploymentsActionCreator.getDeployments(this.context.pageContext, nextProps.definitionId, nextProps.filterState);

            this.setState({
                deployments: [], // Flush out deployments when a new RD is chosen
                isLoading: true,
            });
        }
        else if (this._hasFilterStateChanged(nextProps.filterState)) {
            this._deploymentsActionCreator.getDeployments(this.context.pageContext, nextProps.definitionId, nextProps.filterState);
        }
    }

    public componentDidUpdate(prevProps: IDeploymentsComponentProps, prevState: IDeploymentsState): void {
        // If the list of deployments for the same definition is loaded, check if there is space to load more
        if (prevProps.definitionId === this.props.definitionId &&
            prevState.isLoading
            && !this.state.isLoading) {
            setTimeout(() => this._fetchMoreDeploymentsIfThereIsSpace(), 750); // Wait for 750 ms to sufficiently allow virtualized rendering to complete  
        }

        if (this.state.isLoading) {
            setTimeout(() => {
                if (!!this._loadMoreSpinner) {
                    this._loadMoreSpinner.scrollIntoView({
                        behavior: "smooth",
                        block: "end",
                        inline: "nearest"
                    });
                }
            }, 300); // 300ms because the spinner animation takes time to render

            this._unsubscribeFromPipeline(prevProps.definitionId);
            this._subscribeToPipeline(this.props.definitionId);
        }

        if (this.state.isLoading === false && prevProps.endPerfScenarioCallback) {
            this.setTimeout(() => prevProps.endPerfScenarioCallback(), 0);
        }
    }

    public componentWillUnmount() {
        this._deploymentsStore.removeChangedListener(this._onDeploymentsStoreUpdate);

        const signalRHub = getReleasesSignalRHub();
        if (signalRHub) {
            signalRHub.removeSignalRHanlder(this._signalRHandler);
        }

        this._unsubscribeFromPipeline(this.props.definitionId);
    }

    public render(): React.ReactNode {
        if (this.state.deployments && this.state.deployments.length > 0) {
            return (
                < div
                    className={"deployments-list-holder"}
                    ref={(listHolder) => { this._deploymentsListHolder = listHolder; }}
                    onScroll={this._onDeploymentsListScroll}
                >

                    <DeploymentsDetailsList
                        deployments={this.state.deployments} />
                    {
                        this.state.isLoading && (
                            <div className={"deployments-load-more"} ref={(spinner) => { this._loadMoreSpinner = spinner; }}>
                                <Spinner
                                    className="deployments-spinner loading-more"
                                    size={SpinnerSize.large}
                                    label={Resources.LoadingDeploymentsSpinnerLabel} />
                            </div>)
                    }

                </div >
            );
        }
        else if (this.state.isLoading) {
            return (
                <Spinner
                    className="deployments-spinner"
                    size={SpinnerSize.large}
                    label={Resources.LoadingDeploymentsSpinnerLabel} />
            );
        }
        else {
            return this._getNoReleasesView();
        }
    }

    private _fetchMoreDeploymentsIfThereIsSpace(): void {
        if (this._deploymentsListHolder) {
            // If there is no scroll bar, ie clientHeight = scrollHeight
            if (this._deploymentsListHolder.clientHeight === this._deploymentsListHolder.scrollHeight) {
                this._fetchMoreDeployments();
            }
        }
    }


    private _onDeploymentsListScroll = (event: React.UIEvent<HTMLDivElement>): void => {
        clearTimeout(_onScrollRerenderTimeoutId);
        _onScrollRerenderTimeoutId = setTimeout(() => this.setState({}), 200 /* re render after 200 millisec */);

        if (!!this._deploymentsListHolder && this.state.continuationToken > 0) {
            let scrollPositionFromBottom = this._deploymentsListHolder.clientHeight + this._deploymentsListHolder.scrollTop - this._deploymentsListHolder.scrollHeight;
            if (scrollPositionFromBottom >= -20 && scrollPositionFromBottom <= 20) {
                this._fetchMoreDeployments();
            }
        }
    }

    private _getNoReleasesView(): React.ReactNode {
        const secondaryTextElement: JSX.Element = (
            <div className="active-rd-zero-releases-text">
                <FormatComponent format={Resources.NoReleasesFoundSecondaryText}>
                    <Link rel={"noopener noreferrer"} href={"https://docs.microsoft.com/en-us/vsts/pipelines/release/triggers?view=vsts"} target={"_blank"} className={"setup-triggers-link"}>
                        {Resources.SetupTriggersLinkText}
                    </Link>
                </FormatComponent>
            </div>
        );

        return (
            <div className={css("active-rd-no-releases-view flex flex-grow flex-column flex-center", this.props.cssClass)}>
                <ZeroData
                    primaryText={Resources.NoDeploymentsFoundPrimaryText}
                    secondaryText={secondaryTextElement}
                    imageAltText={Resources.NoDeploymentsFoundPrimaryText}
                    imagePath={getAssetLocation(this.context.pageContext, "ms.vss-releaseManagement-web/release-landing/zerodata-release-management-new.png")}
                />
            </div>);
    }

    private _getListKey(deployment: Deployment): string {
        return format("deployments-list-deploymentid-{0}", deployment.id);
    }

    private _onDeploymentsStoreUpdate = (): void => {
        let storeState: IDeploymentsState = this._deploymentsStore.getState();
        this.setState(storeState);

        if (storeState.deployments) {
            Utils_Accessibility.announce(format(Resources.AnnounceDeploymentCount, storeState.deployments.length));
        }
    }

    private _getLoadingState(keepDeployments: boolean): IDeploymentsState {
        return {
            definitionId: this.props.definitionId,
            deployments: (keepDeployments && this.state.deployments) || [],
            isLoading: true,
            continuationToken: (keepDeployments && this.state.continuationToken) || 0,
        } as IDeploymentsState;
    }

    private _fetchMoreDeployments(): void {
        if (!this.state.isLoading && this.state.continuationToken > 0) {
            this._deploymentsActionCreator.fetchMoreDeployments(this.context.pageContext, this.props.definitionId, this.props.filterState, this.state.continuationToken);
            this.setState(this._getLoadingState(true));
        }
    }

    private _hasFilterStateChanged(newFilterState: IDeploymentsFilterState): boolean {
        if (this.props.filterState) {
            const currentFilterState = this.props.filterState;
            if (equals(newFilterState.branch || "", currentFilterState.branch || "", true) &&
                equals(newFilterState.createdBy || "", currentFilterState.createdBy || "", true) &&
                newFilterState.environmentId === currentFilterState.environmentId) {
                return false;
            }
        }

        return true;
    }

    private _subscribeToPipeline(definitionId: number): void {
        const signalRHub = getReleasesSignalRHub()
        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (signalRHub && tfsPageData && tfsPageData.project) {
            signalRHub.watchReleaseDefinitionsReleases(tfsPageData.project.id, [definitionId]);
        }
    }

    private _unsubscribeFromPipeline(definitionId: number): void {
        const signalRHub = getReleasesSignalRHub()
        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (signalRHub && tfsPageData && tfsPageData.project) {
            signalRHub.stopWatchingReleaseDefinitionsReleases(tfsPageData.project.id, [definitionId]);
        }
    }

    private _deploymentsActionCreator: DeploymentsActionCreator;
    private _deploymentsStore: DeploymentsStore;
    private _deploymentsListHolder: HTMLDivElement | null;
    private _loadMoreSpinner: HTMLDivElement | null;
    private _signalRHandler: IReleasesSignalRHandler;
}

class DeploymentsListSignalRHandler implements IReleasesSignalRHandler {
    constructor(private _pageContext: IVssPageContext, private _actionCreator: DeploymentsActionCreator, private _store: DeploymentsStore) { }

    public onReleaseEnvironmentStatus(definitionId: number, releaseId: number, environmentId: number, environmentStatus: EnvironmentStatus, deploymentStatus: DeploymentStatus, operationStatus: DeploymentOperationStatus): void {
        const state = this._store.getState();
        if (state.definitionId === definitionId && state.definitionId > 0) {
            this._actionCreator.updateReleaseEnvironmentStatus(definitionId, environmentId, deploymentStatus, operationStatus);
        }
    }

    public onReleaseCreated(definitionId: number, releaseId: number): void {
        const state = this._store.getState();
        if (state.definitionId === definitionId && state.definitionId > 0) {
            this._actionCreator.getDeploymentsForCreatedRelease(this._pageContext, definitionId, releaseId);
        }
    }

    public onDeployJobStartedEvent(definitionId: number, releaseId: number, definitionEnvironmentId: number, releaseEnvironmentId: number): void {
        const state = this._store.getState();
        if (state.definitionId === definitionId && state.definitionId > 0) {
            const deploymentsForEnvironment = state.deployments.filter(d => d.releaseEnvironment.id === releaseEnvironmentId);

            // If there is a deployment with status = Not deployed, it will be taken care of by release environment status update.
            // If there aren't any, it means that a new entry needs to be added
            if (!deploymentsForEnvironment.some(d => d.deploymentStatus === DeploymentStatus.NotDeployed)) {
                this._actionCreator.handleDeploymentStarted(this._pageContext, definitionId, releaseId, releaseEnvironmentId);
            }
        }
    }

    public onReleaseApprovalPending(definitionId: number, releaseId: number, releaseEnvironmentId: number, approvalId: number, approverId: string): void {
        const state = this._store.getState();
        if (state.definitionId === definitionId && state.definitionId > 0) {
            if (state.deployments.some(dep => dep.releaseEnvironment.id === releaseEnvironmentId)) {
                // Case - post deploy approval pending event received
                this._actionCreator.updateReleaseEnvironmentStatus(definitionId, releaseEnvironmentId, DeploymentStatus.InProgress, DeploymentOperationStatus.Pending);
            }
            else {
                // Case - pre deploy approval pending event received
                this._actionCreator.handleDeploymentStarted(this._pageContext, definitionId, releaseId, releaseEnvironmentId);
            }
        }
    }
}